import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, Navigate } from 'react-router';
import { useMsal } from '@azure/msal-react';
import * as Sentry from '@sentry/react';
import { Form, Input, InputPassword, Button } from '../../components/ui';
import { closeNotification } from '../../helpers/notifications/notificationService';
import renderNotification from '../../helpers/notifications/renderNotification';
import { loginRequest } from '../../config/msal';

import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import { findMetaDataDescriptionByProperty } from '../../helpers/utility';
import { DEFAULT_RULES } from '../../consts/formRules';
import {
  selectCurrentUser,
  selectLoginWithPasswordLoading,
  selectLoginWithPasswordFailed,
  selectIsLoggedIn,
  selectLoginWithMicrosoftLoading
} from '../../redux/authentication/selectors';
import { selectConfig } from '../../redux/config/selectors';
import { loginWithPassword } from '../../redux/authentication/slice';
import { IconMicrosoft } from '../../components/ui/Icons';
import {
  LoginContentWrapper,
  LoginSectionWrapper,
  LoginFormWrapper,
  LoginForgotPasswordWrapper,
  LoginButtonWrapper,
  LoginWelcomMessage,
  LoginFormBackground,
  LoginDivider,
  LoginDividerText
} from './Login.style';
import ForgotPassword from './ForgotPassword';

/**
 * Functional component for the login process.
 * Allows users to log in with their email and password, and provides a link to set password.
 * @property {Boolean} isLoggedIn - boolean indicating if the currentUser is logged in.
 * @property {Boolean} loginWithPasswordLoading - boolean indicating if the login process is loading.
 * @property {Boolean} loginWithPasswordFailed - boolean indicating if there was an error during login.
 * @property {Boolean} loginWithMicrosoftLoading - boolean indicating if the Microsoft login process is loading.
 * @property {Function} login - function to trigger the login process.
 * @property {Object} currentUser - the current logged-in currentUser information.
 */
const Login = ({
  isLoggedIn,
  loginWithPasswordLoading,
  loginWithPasswordFailed,
  loginWithMicrosoftLoading,
  loginWithPassword,
  currentUser,
  config
}) => {
  const { instance } = useMsal();

  // Set currentUser data for Sentry, once its available
  useEffect(() => {
    if (currentUser) {
      const { id, role, organization_id } = currentUser;
      const organization_type = findMetaDataDescriptionByProperty(currentUser, 'organization_type', '-', 'description');
      Sentry.setUser({ id, role, organization_id, organization_type });
    }
  }, [currentUser]);

  const [form] = Form.useForm();
  const { validateFields } = form;

  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);

  // Remove notifications related to forgot password on login
  const removeForgotPassWordNotificationsOnLogin = () => {
    closeNotification('notification-forgot-password');
  };

  // Handle form submission and trigger login process
  const handleLogin = () => {
    validateFields()
      .then((values) => {
        if (loginWithPasswordLoading || loginWithMicrosoftLoading) {
          return;
        }
        loginWithPassword({
          username: values.username,
          password: values.password,
          callback: removeForgotPassWordNotificationsOnLogin
        });
      })
      .catch((error) => {
        const { input_is_not_valid_email } = NOTIFICATION_MESSAGES;
        const { username, password } = error?.values;
        if (username && password) {
          return renderNotification({ type: 'error', message: input_is_not_valid_email });
        }
      });
  };

  // Toggle the forgot password modal visibility
  const handleToggleForgotPassword = () => setForgotPasswordVisible(!forgotPasswordVisible);

  // Handle Microsoft SSO login
  const handleMicrosoftLogin = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch {
      renderNotification({ type: 'error', message: 'Microsoft-Anmeldung fehlgeschlagen' });
    }
  };

  // this is used to login if you press enter inside the password field
  // some users requested this feature
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  const location = useLocation(); // Use useLocation to get the location object
  const { from } = location.state || { from: { pathname: '/' } };
  const showMicrosoftLogin = config?.any_org_has_allowed_session_type_microsoft;
  const isLoginSuccess = !loginWithPasswordLoading && isLoggedIn && !loginWithPasswordFailed;

  return isLoginSuccess ? (
    <Navigate to={from} replace />
  ) : (
    <LoginContentWrapper>
      <LoginWelcomMessage>
        <span>Willkommen zu Ihrem Versicherungsportal.</span>
      </LoginWelcomMessage>
      <LoginFormBackground>
        <LoginSectionWrapper>
          <LoginFormWrapper>
            <Form form={form} requiredMark="optional">
              <Form.Item
                name="username"
                label="E-Mail"
                className="login-form-item"
                validateTrigger="onSubmit"
                rules={DEFAULT_RULES.EMAIL}
                onKeyDown={handleKeyDown}
              >
                <Input $hasErrors={loginWithPasswordFailed} autoComplete="email" />
              </Form.Item>
              <Form.Item label="Passwort" name="password" rules={DEFAULT_RULES.INPUT_REQUIRED}>
                <InputPassword status={loginWithPasswordFailed ? 'error' : ''} onKeyDown={handleKeyDown} />
              </Form.Item>
              <LoginForgotPasswordWrapper>
                <Button onClick={handleToggleForgotPassword} type="link">
                  Passwort vergessen?
                </Button>
              </LoginForgotPasswordWrapper>
              <LoginButtonWrapper>
                <Button onClick={handleLogin} block>
                  Anmelden
                </Button>
              </LoginButtonWrapper>
            </Form>
          </LoginFormWrapper>
        </LoginSectionWrapper>
        {showMicrosoftLogin && (
          <>
            <LoginDivider>
              <LoginDividerText>oder</LoginDividerText>
            </LoginDivider>
            <LoginSectionWrapper>
              <Button
                type="secondary"
                block
                onClick={handleMicrosoftLogin}
                loading={loginWithMicrosoftLoading}
                icon={<IconMicrosoft />}
              >
                Mit Microsoft anmelden
              </Button>
            </LoginSectionWrapper>
          </>
        )}
      </LoginFormBackground>
      {forgotPasswordVisible && (
        <ForgotPassword
          forgotPasswordVisible={forgotPasswordVisible}
          cancelForgotPassword={handleToggleForgotPassword}
        />
      )}
    </LoginContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  isLoggedIn: selectIsLoggedIn(state),
  loginWithMicrosoftLoading: selectLoginWithMicrosoftLoading(state),
  loginWithPasswordLoading: selectLoginWithPasswordLoading(state),
  loginWithPasswordFailed: selectLoginWithPasswordFailed(state),
  currentUser: selectCurrentUser(state),
  config: selectConfig(state)
});

const mapDispatchToProps = { loginWithPassword };

export default connect(mapStateToProps, mapDispatchToProps)(Login);
